import { getEWSMailMIME, getMailMIME, parseResponse } from '@data';
import {
  encodeBase64,
  getIsOnPremises,
  getMailId,
  getOriginalMailId,
  logInfo,
} from '@utils-global';

/**
 * zero-incident-mail previews an email from its MIME only up to this many decoded bytes
 * (zim.secure_before_you_click.max_mime_bytes), and checks the base64 length against it before
 * decoding. A larger email goes without its MIME, so ZIM previews the body.
 */
const MIME_MAX_BYTES = 5_000_000;
export const MIME_MAX_ENCODED_LENGTH = Math.ceil(MIME_MAX_BYTES / 3) * 4;

/**
 * The protected view opens with window.open from the request's callback, and browsers only allow
 * that shortly after the click. Waiting longer for the MIME would get the view blocked.
 */
export const MIME_TIMEOUT_MS = 5_000;

type FetchSecureBeforeYouClickMimeParams = {
  mailboxEmail: string;
  isSharedMailbox: boolean;
};

const fromGraph = async (mailboxEmail: string, isSharedMailbox: boolean) => {
  const mailId = getMailId();

  if (!mailId) {
    return undefined;
  }

  const { data } = await getMailMIME(mailId, mailboxEmail, isSharedMailbox);

  // Checked before encoding, so an email far over the ceiling is not copied into base64 for nothing.
  if (!data || data.length > MIME_MAX_BYTES) {
    return undefined;
  }

  return encodeBase64(data);
};

/** EWS already answers with the MIME in base64, so it goes to ZIM as it came. */
const fromEws = () =>
  new Promise<string | undefined>((resolve) => {
    const originalMailId = getOriginalMailId();

    if (!originalMailId) {
      resolve(undefined);
      return;
    }

    getEWSMailMIME(originalMailId, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        resolve(undefined);
        return;
      }

      const [mimeContent] = parseResponse(asyncResult.value, ['MimeContent']);

      resolve(mimeContent ? mimeContent.replace(/\s+/g, '') : undefined);
    });
  });

/**
 * The whole email, base64-encoded, so ZIM can show its inline images and attachments. Reuses the
 * report flow's fetchers: Graph in the cloud, EWS on premises. Never throws and never shows a
 * toast: without the MIME the protected view still opens, built from the body.
 */
export const fetchSecureBeforeYouClickMime = async ({
  mailboxEmail,
  isSharedMailbox,
}: FetchSecureBeforeYouClickMimeParams): Promise<string | undefined> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  try {
    const timeout = new Promise<undefined>((resolve) => {
      timer = setTimeout(() => resolve(undefined), MIME_TIMEOUT_MS);
    });
    const mime = await Promise.race([
      getIsOnPremises() ? fromEws() : fromGraph(mailboxEmail, isSharedMailbox),
      timeout,
    ]);

    if (!mime || mime.length > MIME_MAX_ENCODED_LENGTH) {
      return undefined;
    }

    return mime;
  } catch (error) {
    // Only the reason: the MIME is the email itself and stays out of the logs.
    logInfo('Secure Before You Click continues without the MIME', {
      reason: error instanceof Error ? error.message : String(error),
    });

    return undefined;
  } finally {
    clearTimeout(timer);
  }
};
